import { createClient } from '@supabase/supabase-js';
import { Database } from '../types/supabase';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Create a single supabase client for interacting with your database
export const supabase = createClient<Database>(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
    },
    global: {
      headers: {
        'x-client-info': 'supabase-js/2.x',
      },
    },
  }
);

/**
 * Implements exponential backoff retry logic
 * @param fn The async function to retry
 * @param retries Number of retries
 * @param baseDelay Base delay in milliseconds
 */
const retry = async <T>(
  fn: () => Promise<T>,
  retries = 3,
  baseDelay = 1000
): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    if (retries === 0) throw error;
    const delay = baseDelay * Math.pow(2, 3 - retries);
    await new Promise(resolve => setTimeout(resolve, delay));
    return retry(fn, retries - 1, baseDelay);
  }
};

/**
 * Check if the Supabase connection is working
 * This is used to determine if the app should operate in offline mode
 */
export const checkSupabaseConnection = async (): Promise<boolean> => {
  try {
    // Check if we have valid Supabase credentials
    if (!supabaseUrl || !supabaseAnonKey) {
      console.warn('Missing Supabase credentials');
      return false;
    }

    // Try to fetch a single row from the profiles table with retry mechanism
    return await retry(async () => {
      const { error } = await supabase
        .from('profiles')
        .select('id')
        .limit(1)
        .maybeSingle();
      
      // If we get a permissions error, that's okay - it means Supabase is responding
      if (error) {
        if (error.code === 'PGRST204' || error.code === 'PGRST301') {
          return true;
        }
        console.error('Supabase connection error:', error.message);
        return false;
      }
      
      return true;
    });
  } catch (error) {
    if (error instanceof Error) {
      console.error('Error checking Supabase connection:', error.message);
    } else {
      console.error('Error checking Supabase connection:', error);
    }
    return false;
  }
};

/**
 * Check if offline mode is enabled
 * This is a helper function to determine if we should use local storage or Supabase
 */
export const isOfflineMode = async (): Promise<boolean> => {
  // First check if we have a connection to Supabase
  const isConnected = await checkSupabaseConnection();
  
  // If we're not connected, we're in offline mode
  if (!isConnected) {
    forceOfflineMode();
    return true;
  }
  
  // Otherwise, check if offline mode is explicitly enabled
  return localStorage.getItem('offline_mode_enabled') === 'true';
};

/**
 * Check if offline mode is enabled (synchronous version)
 * This is used when we need a synchronous check without awaiting
 */
export const isOfflineModeEnabled = (): boolean => {
  // Check if offline mode is explicitly enabled
  return localStorage.getItem('offline_mode_enabled') === 'true';
};

/**
 * Force offline mode to be enabled
 * This is useful when we know we're going to have authentication issues
 */
export const forceOfflineMode = (): void => {
  localStorage.setItem('offline_mode_enabled', 'true');
};

// Initialize connection check with retry mechanism
(() => {
  checkSupabaseConnection().then(isConnected => {
    if (!isConnected) {
      forceOfflineMode();
      console.warn('Supabase connection failed, switching to offline mode');
    }
  });
})();